/**
 * Anonymous Cart Service
 * Handles cart operations for non-authenticated users using sessionStorage
 */

import { CartItemInput } from './cartTypes';

const ANONYMOUS_CART_KEY = 'anonymousCart';

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('accessToken');
};

/**
 * Get anonymous cart items from sessionStorage
 */
export const getAnonymousCart = (): CartItemInput[] => {
  try {
    const stored = sessionStorage.getItem(ANONYMOUS_CART_KEY);
    if (!stored) return [];
    
    const items = JSON.parse(stored) as Array<
      Omit<CartItemInput, 'startDateTime' | 'endDateTime'> & { 
        startDateTime: string; 
        endDateTime: string;
      }
    >;
    
    // Parse date strings back to Date objects
    return items.map((item) => ({
      ...item,
      startDateTime: new Date(item.startDateTime),
      endDateTime: new Date(item.endDateTime),
    }));
  } catch (error) {
    console.error('Failed to parse anonymous cart:', error);
    return [];
  }
};

/**
 * Save anonymous cart items to sessionStorage
 */
export const setAnonymousCart = (items: CartItemInput[]): void => {
  try {
    sessionStorage.setItem(ANONYMOUS_CART_KEY, JSON.stringify(items));
  } catch (error) {
    console.error('Failed to save anonymous cart:', error);
  }
};

/**
 * Clear anonymous cart from sessionStorage
 */
export const clearAnonymousCart = (): void => {
  try {
    sessionStorage.removeItem(ANONYMOUS_CART_KEY);
  } catch (error) {
    console.error('Failed to clear anonymous cart:', error);
  }
};

/**
 * Add item to anonymous cart
 */
export const addItemToAnonymousCart = (item: CartItemInput): void => {
  const items = getAnonymousCart();
  items.push(item);
  setAnonymousCart(items);
};

/**
 * Remove item from anonymous cart by index
 */
export const removeItemFromAnonymousCart = (index: number): void => {
  const items = getAnonymousCart();
  items.splice(index, 1);
  setAnonymousCart(items);
};

/**
 * Get count of items in anonymous cart
 */
export const getAnonymousCartCount = (): number => {
  return getAnonymousCart().length;
};

/**
 * Calculate total price of anonymous cart
 */
export const getAnonymousCartTotal = (): number => {
  const items = getAnonymousCart();
  return items.reduce((sum, item) => sum + item.price, 0);
};

